import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Center,
  Spinner,
  Button,
  Image,
  IconButton,
  Flex,
  Tooltip,
  useColorModeValue,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, HamburgerIcon, CopyIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { ConfigService } from '../../services/configService';
import { CollectionService } from '../../services/collectionService';
import { Config, Collection } from '../../types/config';

interface DocumentSelectorProps {
  onConfigSelect?: (config: Config) => void;
  selectedConfigId?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  // Shared collection mode props
  sharedMode?: boolean;
  sharedCollection?: Collection;
  sharedConfigs?: Config[];
}

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  onConfigSelect,
  selectedConfigId,
  isCollapsed = false,
  onToggleCollapse,
  sharedMode = false,
  sharedCollection,
  sharedConfigs = [],
}) => {
  const [configs, setConfigs] = useState<Config[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('all-documents');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const selectedBg = useColorModeValue('primary.50', 'primary.900');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    console.log('🔄 DocumentSelector useEffect triggered', { sharedMode, sharedConfigsLength: sharedConfigs?.length, sharedCollectionName: sharedCollection?.name });
    
    if (sharedMode) {
      // In shared mode, use provided configs and collection
      if (sharedConfigs && sharedConfigs.length > 0) {
        setConfigs(sharedConfigs);
        setLoading(false);
      } else if (sharedConfigs) {
        // Empty array provided, stop loading
        setConfigs([]);
        setLoading(false);
      } else {
        // No configs provided yet, keep loading
        setLoading(true);
      }
      
      if (sharedCollection) {
        setCollections([sharedCollection]);
        setSelectedCollectionId(sharedCollection.id);
      }
    } else {
      // Normal mode - load configs and collections
      console.log('📥 Loading configs and collections...');
      loadConfigs();
      loadCollections();
    }
  }, []); // Temporarily remove dependencies to isolate the issue

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const data = await ConfigService.getAllConfigs();
      const sortedConfigs = data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setConfigs(sortedConfigs);
    } catch (error: any) {
      toast({
        title: 'Error loading configurations',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadCollections = async () => {
    try {
      const loadedCollections = await CollectionService.getCollections();
      setCollections(loadedCollections);
      
      // Set default collection as selected if not already set
      if (selectedCollectionId === 'all-documents') {
        const defaultCollection = loadedCollections.find(c => c.isDefault);
        if (defaultCollection) {
          setSelectedCollectionId(defaultCollection.id);
        }
      }
    } catch (error: any) {
      console.error('Error loading collections:', error);
      // Fallback to default collection on error
      setCollections([{ id: 'all-documents', name: 'All Documents', description: '', isDefault: true, documentIds: [], createdAt: new Date(), updatedAt: new Date() }]);
    }
  };

  const handleConfigSelect = async (config: Config) => {
    try {
      // Update local configs: only selected config is active
      const updatedConfigs = configs.map(c => ({
        ...c,
        isActive: c.id === config.id
      }));
      setConfigs(updatedConfigs);
      
      // Call the parent callback with the selected config
      onConfigSelect?.(config);
    } catch (error: any) {
      toast({
        title: 'Error selecting configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const filteredConfigs = (() => {
    let baseConfigs = configs.filter(config => {
      // Only show enabled documents
      if (config.isEnabled === false) return false;
      
      // Filter by search term
      const matchesSearch = config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        config.description.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
      
      // Filter by selected collection
      if (selectedCollectionId === 'all-documents') {
        return true; // "All Documents" shows all documents
      }
      
      const selectedCollection = collections.find(c => c.id === selectedCollectionId);
      if (!selectedCollection) return true;
      
      return selectedCollection.documentIds.includes(config.id);
    });

    // Sort by collection order if a specific collection is selected
    if (selectedCollectionId !== 'all-documents') {
      const selectedCollection = collections.find(c => c.id === selectedCollectionId);
      if (selectedCollection) {
        // Sort configs according to the order in the collection's documentIds
        baseConfigs.sort((a, b) => {
          const indexA = selectedCollection.documentIds.indexOf(a.id);
          const indexB = selectedCollection.documentIds.indexOf(b.id);
          return indexA - indexB;
        });
      }
    }
    // For "All Documents", keep the global order (already sorted by order property)
    
    return baseConfigs;
  })();

  if (loading) {
    return (
      <Center h="200px">
        <VStack spacing={4}>
          <Spinner size="lg" color="primary.500" />
          <Text color={mutedColor}>Loading documents...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <VStack spacing={0} align="stretch" h="full">
      {/* Header with Toggle Button */}
      <Flex
        p={3}
        borderBottom="1px"
        borderColor={borderColor}
        align="center"
        justify="space-between"
        bg={bgColor}
        flexShrink={0}
      >
        <IconButton
          aria-label="Toggle sidebar"
          icon={<HamburgerIcon />}
          size="sm"
          variant="ghost"
          onClick={onToggleCollapse}
        />
        {!isCollapsed && (
          <Menu>
            <MenuButton
              as={Box}
              flex="1"
              ml={2}
              cursor="pointer"
              _hover={{ bg: hoverBg }}
              borderRadius="md"
              px={2}
              py={1}
              transition="all 0.2s"
            >
              <HStack spacing={2} justify="space-between">
                <HStack spacing={2}>
                  <CopyIcon boxSize={4} color={textColor} />
                  <Text fontSize="sm" fontWeight="medium" color={textColor} noOfLines={1}>
                    {collections.find(c => c.id === selectedCollectionId)?.name || 'Documents'}
                  </Text>
                </HStack>
                <ChevronDownIcon boxSize={4} color={mutedColor} />
              </HStack>
            </MenuButton>
            <Portal>
              <MenuList 
                zIndex={1500}
                maxW="250px"
                borderColor={borderColor}
                bg={bgColor}
                boxShadow="lg"
              >
                {collections.map(collection => (
                  <MenuItem
                    key={collection.id}
                    onClick={() => setSelectedCollectionId(collection.id)}
                    bg={collection.id === selectedCollectionId ? selectedBg : 'transparent'}
                    _hover={{ bg: hoverBg }}
                    _focus={{ bg: hoverBg }}
                  >
                    <HStack spacing={3} w="full">
                      <CopyIcon boxSize={3} color={mutedColor} />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontSize="sm" fontWeight="medium" color={textColor}>
                          {collection.name}
                        </Text>
                        <Text fontSize="xs" color={mutedColor}>
                          {collection.documentIds.length} document{collection.documentIds.length !== 1 ? 's' : ''}
                        </Text>
                      </VStack>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        )}
        {!isCollapsed && (
          <IconButton
            aria-label="Add document"
            icon={<AddIcon />}
            size="sm"
            variant="ghost"
            onClick={() => window.open('/settings', '_blank')}
          />
        )}
      </Flex>

      {/* Search - only show when expanded */}
      {!isCollapsed && (
        <Box p={3} borderBottom="1px" borderColor={borderColor}>
          <InputGroup size="sm">
            <InputLeftElement>
              <SearchIcon color={mutedColor} />
            </InputLeftElement>
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Box>
      )}

      {/* Documents List */}
      <VStack spacing={0} align="stretch" flex="1" overflowY="auto" p={isCollapsed ? 1 : 0}>
        {filteredConfigs.length === 0 ? (
          !isCollapsed && (
            <Center flex="1" p={4}>
              <VStack spacing={3}>
                <Text color={mutedColor} fontSize="sm" textAlign="center">
                  {searchTerm ? 'No documents match your search' : 'No documents available'}
                </Text>
                <Button
                  size="sm"
                  leftIcon={<AddIcon />}
                  colorScheme="primary"
                  variant="outline"
                  onClick={() => window.open('/settings', '_blank')}
                >
                  Create Document
                </Button>
              </VStack>
            </Center>
          )
        ) : (
          filteredConfigs.map((config) => {
            const isSelected = selectedConfigId === config.id;
            
            if (isCollapsed) {
              // Collapsed state - icons only
              return (
                <Tooltip
                  key={config.id}
                  label={`${config.name}${config.description ? ` - ${config.description}` : ''}`}
                  placement="right"
                  hasArrow
                >
                  <Box
                    w="48px"
                    h="48px"
                    mx="auto"
                    mb={2}
                    position="relative"
                    cursor="pointer"
                    transition="all 0.2s"
                    _hover={{
                      transform: 'scale(1.05)',
                    }}
                    onClick={() => handleConfigSelect(config)}
                  >
                    {/* Left strip for selected state */}
                    {isSelected && (
                      <Box
                        position="absolute"
                        left="-8px"
                        top="0"
                        bottom="0"
                        w="5px"
                        bg="primary.500"
                        borderRadius="0 3px 3px 0"
                        boxShadow="2px 0 4px rgba(43, 166, 43, 0.2)"
                        zIndex={2}
                      />
                    )}
                    
                    {config.icon ? (
                      <Image
                        src={config.icon}
                        alt="icon"
                        boxSize="36px"
                        objectFit="contain"
                        borderRadius="12px"
                        border="1px"
                        borderColor={borderColor}
                        bg={isSelected ? selectedBg : "white"}
                        m="6px"
                      />
                    ) : (
                      <Box
                        boxSize="36px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg={isSelected ? "primary.500" : "gray.800"}
                        color="white"
                        fontWeight="bold"
                        fontSize="lg"
                        borderRadius="12px"
                        userSelect="none"
                        m="6px"
                      >
                        {config.name.charAt(0).toUpperCase()}
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            }

            // Expanded state - full content
            return (
              <Box
                key={config.id}
                position="relative"
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  bg: isSelected ? selectedBg : hoverBg,
                }}
                bg={isSelected ? selectedBg : bgColor}
                onClick={() => handleConfigSelect(config)}
                borderBottom="1px"
                borderColor={borderColor}
              >
                {/* Left strip for selected state */}
                {isSelected && (
                  <Box
                    position="absolute"
                    left="0"
                    top="0"
                    bottom="0"
                    w="5px"
                    bg="primary.500"
                    borderRadius="0 3px 3px 0"
                    boxShadow="2px 0 4px rgba(43, 166, 43, 0.2)"
                    zIndex={2}
                  />
                )}
                
                <HStack spacing={3} align="start" p={3} pl={isSelected ? 5 : 3}>
                  {/* Icon */}
                  <Box flexShrink={0}>
                    {config.icon ? (
                      <Image
                        src={config.icon}
                        alt="icon"
                        boxSize="36px"
                        objectFit="contain"
                        borderRadius="md"
                        border="1px"
                        borderColor={borderColor}
                        bg="white"
                      />
                    ) : (
                      <Box
                        boxSize="36px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        bg={isSelected ? "primary.500" : "gray.800"}
                        color="white"
                        fontWeight="bold"
                        fontSize="lg"
                        borderRadius="md"
                        userSelect="none"
                      >
                        {config.name.charAt(0).toUpperCase()}
                      </Box>
                    )}
                  </Box>

                  {/* Content */}
                  <VStack align="start" spacing={1} flex="1" minW="0">
                    <Text
                      fontWeight={isSelected ? "bold" : "medium"}
                      color={isSelected ? "primary.600" : textColor}
                      fontSize="sm"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      w="full"
                    >
                      {config.name}
                    </Text>
                    
                    {config.description && (
                      <Text
                        fontSize="xs"
                        color={mutedColor}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        w="full"
                        lineHeight="1.3"
                      >
                        {config.description}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Box>
            );
          })
        )}
      </VStack>
    </VStack>
  );
};